import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {get} from 'lodash';
import {filter} from 'rxjs/operators';
import {TreeNode} from '../../models/rme/tree-node';

import {
  IntercomEventService,
  IntercomEventsByArea,
  NodeStatus,
  PermissionsService,
  REF_MODEL_STORE,
  RefModelNode,
  RefModelStoreService,
  Refmodel,
  TakeUntilComponentDestroy,
  User,
  constants
} from '@q9elements/ui-kit/common';
import {ContextMenuItem} from '@q9elements/ui-kit/components';
import {SUPPORTED_METADATA_TYPES} from '../../code-search/models/enum/code-search-metadata.enum';
import {CodeSearchTypesEnum} from '../../code-search/models/enum/code-search-types.enum';
import {GridSelectorService} from '../../custom-grid/services/grid-selector.service';
import {UpdateNodesAction} from '../../editor-interface/components/update-tags-dialog/models/update-tags.interface';
import {NewNodeTypes} from '../../editor-interface/models/enums/NodeTypes.enum';
import {AttachNodeService} from '../../editor-interface/services/attach-node.service';
import {AttachmentDialogTypesEnum} from '../models/context-menu.enum';
import {CONTEXT_MENU_ITEMS, CONTEXT_MENU_TRACK_VALUES} from '../models/context-menu.interface';
import {ContextMenuActionsService} from './context-menu-actions.service';

const ADD_CHANGE_LIMIT = 50;

@Injectable()
export class ContextMenuItemsService extends TakeUntilComponentDestroy {
  private rootData: any;
  private selectedNode: any;
  private refModel: Refmodel;
  private areNodesDeleting: boolean;
  private canPasteOrgModelNode: boolean;

  public isExternalRefModel: boolean;
  public selectedItemsCount = 0;

  constructor(
    private rmeStore: RefModelStoreService,
    private router: Router,
    private contextMenuActionsService: ContextMenuActionsService,
    private gridSelectorService: GridSelectorService,
    private translate: TranslateService,
    private permissionsService: PermissionsService,
    private intercomEventService: IntercomEventService,
    private attachNodeService: AttachNodeService
  ) {
    super();

    this.initRootDataSubscription();
    this.initRefModelStoreSubscription();
    this.initSelectedNodeStoreSubscription();
    this.initAreNodesDeletingSubscription();
    this.subscribeToSelectedItems();
  }

  get user(): User {
    return this.rmeStore.user;
  }

  get isStripped(): boolean {
    return this.rmeStore.isStripped;
  }

  get isPreview(): boolean {
    return !this.rmeStore.isAuthor;
  }

  get isSelectedNodeIdEqualToRefModelId() {
    return this.selectedNode?._id === this.refModel?._id;
  }

  get isSelectedNodeDeleted() {
    return this.selectedNode?.status === 'deleted';
  }

  get isSelectedNodeIdEqualToRootDataId() {
    return this.selectedNode?._id === this.rootData?._id;
  }

  get isRootNode() {
    return this.selectedNode?._id === this.rmeStore.rootData?._id;
  }

  get typeOfNode(): string {
    let typeOfNode = constants.RSB_LEVELS.ROOT;
    const isRefModel = this.selectedNode?._id === this.refModel._id;
    const refShow = !this.selectedNode?.sf && !isRefModel;
    const isProposedNode = this.selectedNode?.status === NodeStatus.PROPOSED;
    const sfShow =
      this.selectedNode?.sf &&
      this.selectedNode?.parentPath &&
      this.selectedNode.sf?.apiName &&
      !isRefModel;
    const isPro = this.permissionsService.isProSpace();

    const result = isPro && (refShow || sfShow);

    if (result || isProposedNode) {
      typeOfNode = constants.RSB_LEVELS.NODE;
    }

    if (isRefModel) {
      typeOfNode = constants.RSB_LEVELS.ROOT;
    }

    if ((result === null && refShow) || result === null) {
      typeOfNode = constants.RSB_LEVELS.GROUP;
    }

    return typeOfNode;
  }

  get isNodeLevel(): boolean {
    return this.typeOfNode === constants.RSB_LEVELS.NODE;
  }

  get canChangeNode() {
    return (
      !this.isStripped &&
      !this.isPreview &&
      !this.isSelectedNodeIdEqualToRefModelId &&
      (!this.isSelectedNodeDeleted || !this.areNodesDeleting)
    );
  }

  get showMakeNodeRootItem() {
    return !this.isRootNode;
  }

  get showOpenImpactModeItem() {
    return this.isNodeLevel && !this.isSelectedNodeIdEqualToRefModelId && !this.isPreview;
  }

  get showDependencyOrgNodeItem() {
    return this.attachNodeService.q9canPasteOrgNode();
  }

  get showAddNodeItem() {
    return (
      !this.refModel.external &&
      !this.isPreview &&
      (!this.isSelectedNodeDeleted || !this.areNodesDeleting)
    );
  }

  get showAddSiblingNodeItem() {
    return (
      !this.isSelectedNodeIdEqualToRefModelId &&
      (!this.rootData || !this.isSelectedNodeIdEqualToRootDataId)
    );
  }

  get showCopyLinkItem() {
    return !this.isStripped && !this.isSelectedNodeIdEqualToRefModelId;
  }

  get showAddChangeStory() {
    return this.rmeStore.isAuthor && this.permissionsService.isRequirementsManager();
  }

  get showCodeSearch(): boolean {
    if (!this.selectedNode) {
      return false;
    }

    const haveCodeSearchRights =
      this.isExternalRefModel && this.user.userInTeam.team.plan.proPlus && this.rmeStore.isEditor;

    const type = this.selectedNode?.sf?.type;
    const noManagedPackagesTypes = ['Validation Rules', 'Fields'];

    if (haveCodeSearchRights && noManagedPackagesTypes.includes(type)) {
      return !this.selectedNode.identity.includes('Namespace');
    }

    if (haveCodeSearchRights && type === 'Records') {
      return Boolean(this.selectedNode.nodes.length);
    }

    return (
      haveCodeSearchRights &&
      ((this.isRootNode && this.isSelectedNodeIdEqualToRefModelId) ||
        SUPPORTED_METADATA_TYPES.includes(this.selectedNode.identity))
    );
  }

  get showCodeSearchForSingleNode() {
    if (!this.selectedNode) {
      return false;
    }

    return (
      this.isExternalRefModel &&
      this.user.userInTeam.team.plan.proPlus &&
      this.rmeStore.isEditor &&
      !this.isSelectedNodeIdEqualToRefModelId &&
      this.selectedNode?.status !== NodeStatus.PROPOSED &&
      Boolean(this.selectedNode.sf?.id)
    );
  }

  private initRootDataSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.ROOT_DATA)
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(data => data !== this.rootData)
      )
      .subscribe((data: any) => (this.rootData = Object.assign({}, data)));
  }

  private initRefModelStoreSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.REF_MODEL)
      .pipe(this.takeUntilComponentDestroy(), filter(Boolean))
      .subscribe((data: Refmodel) => {
        this.refModel = data;
        this.isExternalRefModel = this.refModel.external;
      });
  }

  private initSelectedNodeStoreSubscription(): void {
    this.rmeStore
      .select<RefModelNode>(REF_MODEL_STORE.SELECTED_NODE)
      .pipe(this.takeUntilComponentDestroy(), filter(Boolean))
      .subscribe((node: RefModelNode) => {
        this.selectedNode = Object.assign({}, node);
        this.checkCopiedOrgModelNode();
      });
  }

  private initAreNodesDeletingSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.ARE_NODES_DELETING)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((areNodesDeleting: boolean) => (this.areNodesDeleting = areNodesDeleting));
  }

  private subscribeToSelectedItems() {
    this.gridSelectorService.selectedKeys$
      .pipe(this.takeUntilComponentDestroy(), filter(Boolean))
      .subscribe((keys: any[]) => {
        this.selectedItemsCount = keys.length;
      });
  }

  public checkCopiedOrgModelNode(): boolean {
    this.canPasteOrgModelNode = this.attachNodeService.q9canPasteOrgNode();

    return this.canPasteOrgModelNode;
  }

  public getActionsForContextMenuItems(
    idParam = '_id',
    isDependency = false,
    isCodeSearch = false
  ): ContextMenuItem[] {
    return [
      {
        icon: 'list',
        trackValue: isCodeSearch
          ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_ADD_ACTIONS
          : CONTEXT_MENU_ITEMS.ADD_ACTIONS,
        tooltip: CONTEXT_MENU_TRACK_VALUES.ADD_ACTIONS,
        hasShow: () => !this.isPreview,
        items: [
          {
            text: CONTEXT_MENU_ITEMS.OPTIMIZATION_STATUS_UPDATE,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_ASSESSMENT_STATUS_UPDATE
              : CONTEXT_MENU_TRACK_VALUES.ASSESSMENT_STATUS_UPDATE,
            action: (dataItems: TreeNode[]) => {
              const nodeIds = this.getNodeIds(dataItems, idParam);

              return this.contextMenuActionsService.openNodeBulkUpdateDialog(
                nodeIds,
                UpdateNodesAction.OPTIMIZATION_STATUS_UPDATE,
                isDependency
              );
            }
          },
          {
            text: CONTEXT_MENU_ITEMS.ASSIGNEE_UPDATE,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_ASSIGNEE_UPDATE
              : CONTEXT_MENU_TRACK_VALUES.ASSIGNEE_UPDATE,
            action: (dataItems: TreeNode[]) => {
              const nodeIds = this.getNodeIds(dataItems, idParam);

              return this.contextMenuActionsService.openNodeBulkUpdateDialog(
                nodeIds,
                UpdateNodesAction.NODE_ASSIGNEE_UPDATE,
                isDependency
              );
            }
          },
          {
            text: CONTEXT_MENU_ITEMS.METAFIELD_VALUE_UPDATE,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_METAFIELD_VALUE_UPDATE
              : CONTEXT_MENU_TRACK_VALUES.METAFIELD_VALUE_UPDATE,
            action: (dataItems: TreeNode[]) => {
              const nodeIds = this.getNodeIds(dataItems, idParam);

              return this.contextMenuActionsService.openNodeBulkUpdateDialog(
                nodeIds,
                UpdateNodesAction.CNA_UPDATE,
                isDependency
              );
            }
          }
        ]
      }
    ];
  }

  public getDocumentationContextMenuItems(
    idParam: string,
    isCodeSearch = false
  ): ContextMenuItem[] {
    return [
      {
        icon: 'file-plus',
        trackValue: isCodeSearch
          ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_ADD_DOCUMENTATION
          : CONTEXT_MENU_TRACK_VALUES.ADD_DOCUMENTATION,
        tooltip: CONTEXT_MENU_ITEMS.ADD_DOCUMENTATION,
        hasShow: () => this.canChangeNode,
        items: [
          {
            text: CONTEXT_MENU_ITEMS.NOTE,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_NOTE
              : CONTEXT_MENU_TRACK_VALUES.NOTE,
            action: ([dataItem]: [TreeNode]) =>
              this.contextMenuActionsService.openAttachmentDialog(
                dataItem,
                AttachmentDialogTypesEnum.TEXT,
                idParam
              )
          },
          {
            text: CONTEXT_MENU_ITEMS.URL,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_URL
              : CONTEXT_MENU_TRACK_VALUES.URL,
            action: ([dataItem]: [TreeNode]) =>
              this.contextMenuActionsService.openAttachmentDialog(
                dataItem,
                AttachmentDialogTypesEnum.URL,
                idParam
              )
          },
          {
            text: CONTEXT_MENU_ITEMS.DATA_TABLE,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_DATA_TABLE
              : CONTEXT_MENU_TRACK_VALUES.DATA_TABLE,
            action: ([dataItem]: [TreeNode]) =>
              this.contextMenuActionsService.openAttachmentDialog(
                dataItem,
                AttachmentDialogTypesEnum.DATA_TABLE,
                idParam
              )
          },
          {
            text: CONTEXT_MENU_ITEMS.IMAGE,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_IMAGE
              : CONTEXT_MENU_TRACK_VALUES.IMAGE,
            action: ([dataItem]: [TreeNode]) =>
              this.contextMenuActionsService.openAttachmentDialog(
                dataItem,
                AttachmentDialogTypesEnum.IMAGE,
                idParam
              )
          }
        ]
      }
    ];
  }

  public getChangesContextMenuItems(
    idParam: string,
    isTreeGrid = false,
    isCodeSearch = false
  ): ContextMenuItem[] {
    const changeItems = [
      {
        icon: 'changes',
        trackValue: isCodeSearch
          ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_ADD_CHANGES
          : CONTEXT_MENU_TRACK_VALUES.ADD_CHANGES,
        tooltip: CONTEXT_MENU_ITEMS.ADD_CHANGES,
        hasShow: () => this.canChangeNode,
        items: [
          {
            text: CONTEXT_MENU_ITEMS.ADD_EXISTING_STORY,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_ADD_EXISTING_STORY
              : CONTEXT_MENU_TRACK_VALUES.ADD_EXISTING_STORY,
            tooltip: this.translate.instant('CONTEXT_MENU.ADD_CHANGE_DISABLED_TOOLTIP', {
              limit: ADD_CHANGE_LIMIT
            }),
            disabled: this.selectedItemsCount > ADD_CHANGE_LIMIT,
            hasShow: () => true,
            action: rows => {
              const nodeIds = this.getNodeIds(rows, idParam);

              this.contextMenuActionsService.findStory(nodeIds);
            }
          },
          {
            text: CONTEXT_MENU_ITEMS.ADD_NEW_STORY,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_ADD_NEW_STORY
              : CONTEXT_MENU_TRACK_VALUES.ADD_NEW_STORY,
            tooltip: this.translate.instant('CONTEXT_MENU.ADD_CHANGE_DISABLED_TOOLTIP', {
              limit: ADD_CHANGE_LIMIT
            }),
            disabled: this.selectedItemsCount > ADD_CHANGE_LIMIT,
            hasShow: () => this.showAddChangeStory,
            action: (dataItems: TreeNode[]) => {
              const nodeIds = this.getNodeIds(dataItems, idParam);

              this.contextMenuActionsService.createAndAttachStory(nodeIds);
            }
          },
          {
            text: CONTEXT_MENU_ITEMS.ADD_MULTIPLE_STORIES,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_ADD_MULTIPLE_STORIES
              : CONTEXT_MENU_TRACK_VALUES.ADD_MULTIPLE_STORIES,
            hasShow: () =>
              this.showAddChangeStory && this.selectedItemsCount > 1 && idParam !== 'dependentId',
            disabled: this.selectedItemsCount > ADD_CHANGE_LIMIT,
            tooltip: this.translate.instant('CONTEXT_MENU.ADD_CHANGE_DISABLED_TOOLTIP', {
              limit: ADD_CHANGE_LIMIT
            }),
            action: (dataItems: any[]) => {
              if (isCodeSearch) {
                const nodeIds = dataItems.map(item => get(item, idParam)) as [];
                const codeSearchIds = dataItems.map(item => item._id);
                const codeSearchType = dataItems[0].type;

                this.contextMenuActionsService.attachMultipleStoryForCodeSearch(
                  nodeIds,
                  codeSearchIds,
                  codeSearchType
                );
                return;
              }

              const nodeIds = this.getNodeIds(dataItems, idParam);

              this.contextMenuActionsService.createAndAttachStory(nodeIds, true);
            }
          },
          {
            text: CONTEXT_MENU_ITEMS.CREATE_REQUIREMENT,
            trackValue: CONTEXT_MENU_TRACK_VALUES.CREATE_REQUIREMENT,
            hasShow: () => isTreeGrid,
            action: () => this.contextMenuActionsService.openAddRequirementDialog()
          }
        ]
      }
    ];

    const items = changeItems[0].items.filter((item: ContextMenuItem) => item.hasShow());

    if (!items.length) {
      return [];
    }

    return [{...changeItems[0], items}];
  }

  public getMakeNodeRootContextMenuItem(): ContextMenuItem[] {
    return [
      {
        icon: 'component',
        trackValue: CONTEXT_MENU_TRACK_VALUES.MAKE_NODE_ROOT,
        tooltip: 'CONTEXT_MENU.MAKE_COMPONENT_ROOT_TOOLTIP',
        hasShow: () => this.showMakeNodeRootItem,
        action: ([dataItem]: [TreeNode]) =>
          this.contextMenuActionsService.emitMakeNodeRoot(dataItem)
      }
    ];
  }

  public getCodeSearchContextMenuItems(idParam = '_id', isCodeSearch = false): ContextMenuItem[] {
    return [
      {
        icon: 'search',
        trackValue: isCodeSearch
          ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_RUN_CODE_SEARCH
          : CONTEXT_MENU_TRACK_VALUES.RUN_CODE_SEARCH,
        tooltip: this._getCodeSearchTooltip(),
        disabled: this.refModel.syncStatus === 'inProgress',
        hasShow: () => this.showCodeSearch,
        action: ([dataItem]: [TreeNode]) =>
          this.contextMenuActionsService.openCodeSearchDialog(dataItem)
      },
      {
        icon: 'search',
        trackValue: isCodeSearch
          ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_RUN_NODE_CODE_SEARCH
          : CONTEXT_MENU_TRACK_VALUES.RUN_NODE_CODE_SEARCH,
        disabled: this.refModel.syncStatus === 'inProgress',
        tooltip: this._getCodeSearchTooltip(true),
        hasShow: () => this.showCodeSearchForSingleNode,
        action: ([dataItem]: [TreeNode]) => {
          const payload = {
            type: CodeSearchTypesEnum.NODE,
            search: isCodeSearch ? dataItem.foundIn.refModelNode : dataItem?.[idParam]
          };

          this.intercomEventService.sendIntercomEvent(
            this.refModel,
            IntercomEventsByArea.SALESFORCE_METADATA.SEARCH_FOR_REFERENCES_CLICKED
          );

          const url = this.router.serializeUrl(
            this.router.createUrlTree([`code-search`, this.rmeStore.refModel._id], {
              relativeTo: null,
              queryParams: payload
            })
          );

          window.open(url, '_blank');
        }
      }
    ];
  }

  public getCreateNewNodeContextMenuItems(): ContextMenuItem[] {
    return [
      {
        icon: 'text_plus',
        tooltip: CONTEXT_MENU_ITEMS.CREATE_NEW_NODE,
        trackValue: CONTEXT_MENU_TRACK_VALUES.CREATE_NEW_NODE,
        hasShow: () => this.showAddNodeItem,
        items: [
          {
            text: CONTEXT_MENU_ITEMS.CHILD_NODE,
            trackValue: CONTEXT_MENU_TRACK_VALUES.CHILD_NODE,
            action: ([dataItem]: [TreeNode]) =>
              this.contextMenuActionsService.openAddNodeDialog(dataItem, NewNodeTypes.CHILD)
          },
          ...(this.showAddSiblingNodeItem
            ? [
                {
                  text: CONTEXT_MENU_ITEMS.SIBLING_NODE,
                  trackValue: CONTEXT_MENU_TRACK_VALUES.SIBLING_NODE,
                  action: ([dataItem]: [TreeNode]) =>
                    this.contextMenuActionsService.openAddNodeDialog(dataItem, NewNodeTypes.SIBLING)
                }
              ]
            : [])
        ]
      }
    ];
  }

  public getTagsChangeContextMenuItems(
    idParam?: string,
    isDependency = false,
    isCodeSearch = false
  ): ContextMenuItem[] {
    return [
      {
        icon: 'tags',
        trackValue: isCodeSearch
          ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_TAGS
          : CONTEXT_MENU_TRACK_VALUES.TAGS,
        tooltip: CONTEXT_MENU_ITEMS.TAGS,
        hasShow: () => !this.isPreview,
        items: [
          {
            text: CONTEXT_MENU_ITEMS.TAGS_ADD,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_TAGS_ADD
              : CONTEXT_MENU_TRACK_VALUES.TAGS_ADD,
            action: (dataItems: TreeNode[]) => {
              const nodeIds = this.getNodeIds(dataItems, idParam);

              return this.contextMenuActionsService.openNodeBulkUpdateDialog(
                nodeIds,
                UpdateNodesAction.TAGS_ADD,
                isDependency
              );
            }
          },
          {
            text: CONTEXT_MENU_ITEMS.TAGS_REPLACE,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_TAGS_REPLACE
              : CONTEXT_MENU_TRACK_VALUES.TAGS_REPLACE,
            action: (dataItems: TreeNode[]) => {
              const nodeIds = this.getNodeIds(dataItems, idParam);

              return this.contextMenuActionsService.openNodeBulkUpdateDialog(
                nodeIds,
                UpdateNodesAction.TAGS_REPLACE,
                isDependency
              );
            }
          }
        ]
      }
    ];
  }

  public getProposedNodeItem(): ContextMenuItem[] {
    if (this.isExternalRefModel && !this.isPreview && this.selectedNode.childTypes?.length) {
      const {childTypes} = this.selectedNode;
      const [mainType, ...subTypes] = childTypes;
      const type = mainType.sfType.toLowerCase();
      const item: ContextMenuItem = {
        icon: 'text_plus',
        tooltip: CONTEXT_MENU_ITEMS.ADD_PROPOSED_NODE,
        trackValue: this.isExternalRefModel ? CONTEXT_MENU_TRACK_VALUES.ADD_PROPOSED_NODE : null,
        type,
        hasShow: () => true
      };

      if (subTypes.length) {
        item.items = subTypes.map(subType => ({
          text: subType.sfType,
          action: ([dataItem]: [TreeNode]) =>
            this.contextMenuActionsService.openProposedNodeCreatingDialog(dataItem, subType)
        }));
      }

      if (!item.items) {
        item.action = ([dataItem]: [TreeNode]) =>
          this.contextMenuActionsService.openProposedNodeCreatingDialog(dataItem, mainType);
      }

      return [item];
    }

    return [];
  }

  private getNodeIds(dataItems: TreeNode[], idParam = '_id') {
    return [...new Set(dataItems.map(item => get(item, idParam)))] as [];
  }

  public getOpenDependencyGridMenuItem(
    identifier: string,
    isCodeSearch = false
  ): ContextMenuItem[] {
    return [
      {
        icon: 'table-share',
        text: CONTEXT_MENU_ITEMS.OPEN_DEPENDENCY_GRID,
        trackValue: isCodeSearch
          ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_OPEN_DEPENDENCY_GRID
          : CONTEXT_MENU_TRACK_VALUES.OPEN_DEPENDENCY_GRID,
        tooltip: CONTEXT_MENU_ITEMS.OPEN_DEPENDENCY_GRID,
        lockIcon: this.permissionsService.isFree(),
        hasShow: () => !this.isPreview,
        action: (dataItems: any[]) => {
          const nodeIds =
            identifier === 'foundIn.refModelNode'
              ? dataItems.map(item => item.foundIn.refModelNode)
              : dataItems.map(item => item[identifier]);

          this.contextMenuActionsService.openDependencyGrid(nodeIds);
        }
      }
    ];
  }

  public getDependencyContextMenuItems(
    idParam: string,
    isDependency = false,
    isCodeSearch = false
  ): ContextMenuItem[] {
    const dependencySubItems = [
      {
        text: CONTEXT_MENU_ITEMS.ADD_DEPENDENCY,
        trackValue: isCodeSearch
          ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_ADD_DEPENDENCY
          : CONTEXT_MENU_TRACK_VALUES.ADD_DEPENDENCY,
        hasShow: () => this.showDependencyOrgNodeItem,
        action: ([dataItem]: [TreeNode]) =>
          this.contextMenuActionsService.openAttachmentDialog(
            dataItem,
            AttachmentDialogTypesEnum.ORG_MODEL_NODE,
            idParam
          )
      },
      {
        text: CONTEXT_MENU_ITEMS.COPY_NODE,
        trackValue: isCodeSearch
          ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_COPY_NODE
          : CONTEXT_MENU_TRACK_VALUES.COPY_NODE,
        hasShow: () => this.showCopyLinkItem,
        action: ([dataItem]: [TreeNode]) =>
          this.contextMenuActionsService.copyNodeLink(dataItem, idParam)
      },
      ...this.getOpenDependencyGridMenuItem(idParam, isCodeSearch)
    ];

    const dependencyItems = [
      {
        icon: 'tree',
        trackValue: isCodeSearch
          ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_DEPENDENCIES
          : CONTEXT_MENU_TRACK_VALUES.DEPENDENCIES,
        tooltip: CONTEXT_MENU_ITEMS.DEPENDENCIES,
        hasShow: () => !this.isPreview,
        items: [
          ...(!isDependency ? dependencySubItems : []),
          {
            text: CONTEXT_MENU_ITEMS.OPEN_DEPENDENCY_TREE,
            trackValue: isCodeSearch
              ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_OPEN_DEPENDENCY_TREE
              : CONTEXT_MENU_TRACK_VALUES.OPEN_DEPENDENCY_TREE,
            lockIcon: this.permissionsService.isFree(),
            hasShow: () => this.showOpenImpactModeItem,
            action: ([dataItem]: [TreeNode]) => {
              const nodeId = isCodeSearch ? dataItem.foundIn.refModelNode : dataItem?.[idParam];
              this.contextMenuActionsService.openDependencyTree(nodeId);
            }
          }
        ]
      }
    ];

    const items = dependencyItems[0].items.filter((item: ContextMenuItem) => item.hasShow());

    if (!items.length) {
      return [];
    }

    return [{...dependencyItems[0], items}];
  }

  public getShowComponentInMetadataDictionary(
    idParam = '_id',
    isCodeSearch = false
  ): ContextMenuItem[] {
    return [
      {
        icon: 'vocabulary_search',
        trackValue: isCodeSearch
          ? CONTEXT_MENU_TRACK_VALUES.CODE_SEARCH_SHOW_IN_METADATA_DICTIONARY
          : CONTEXT_MENU_TRACK_VALUES.SHOW_IN_METADATA_DICTIONARY,
        tooltip: 'CONTEXT_MENU.SHOW_IN_METADATA_DICTIONARY',
        hasShow: () => true,
        action: ([dataItem]: [TreeNode]) => {
          const nodeId = isCodeSearch ? dataItem.foundIn.refModelNode : dataItem?.[idParam];

          this.contextMenuActionsService.showComponentInMetadataDictionary(nodeId);
        }
      }
    ];
  }

  private _getCodeSearchTooltip(isNodeSearch = false): string {
    if (this.refModel.syncStatus === 'inProgress') {
      return this.translate.instant('CONTEXT_MENU.RUN_CODE_SEARCH_DISABLED_TOOLTIP');
    }

    if (isNodeSearch) {
      return 'CONTEXT_MENU.RUN_NODE_CODE_SEARCH';
    }

    return 'CONTEXT_MENU.RUN_CODE_SEARCH';

    // This code will be used in code search v2
    // if (!this.refModel.codeSearch?.status) {
    //   return this.translate.instant('CONTEXT_MENU.NEED_SYNC_TO_RUN_CODE_SEARCH_TOOLTIP');
    // }
  }
}
